import { Fragment, useState, useEffect, useCallback } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { finanzenApi } from "../services/finanzenApi";

const STATUS_FARBE = { vorgeschlagen: THEME.colors.warning, geprueft: THEME.colors.primary, freigegeben: THEME.colors.success, gesperrt: THEME.colors.danger };
const STATUS_NAME = { vorgeschlagen: "Vorgeschlagen", geprueft: "Geprüft", freigegeben: "Freigegeben", gesperrt: "Gesperrt" };
const KONTOART_NAME = { aufwand: "Aufwand", ertrag: "Ertrag", bestand: "Bestand (Aktiv/Passiv)", privat: "Privat", steuer: "Steuer (USt/Vorsteuer)", debitor: "Debitor", kreditor: "Kreditor", durchlauf: "Durchlaufposten", sonstige: "Sonstige" };

// SKR03-Kontenplan (Phase 2 des Buchhaltungs-Ausbaus): reine Stammdaten-Pruefliste.
// Alle Konten sind VORSCHLAEGE; hier wird nichts kontiert und nichts gebucht.
export default function KontenplanSeite({ benutzerId }) {
  const [konten, setKonten] = useState(null);
  const [zus, setZus] = useState(null);
  const [fehler, setFehler] = useState("");
  const [keinRecht, setKeinRecht] = useState(false);
  const [status, setStatus] = useState("");
  const [art, setArt] = useState("");
  const [suche, setSuche] = useState("");
  const [nurHinweise, setNurHinweise] = useState(false);
  const [offenId, setOffenId] = useState(null);
  const [hinweise, setHinweise] = useState([]);
  const [notiz, setNotiz] = useState("");

  const laden = useCallback(() => {
    setFehler("");
    Promise.all([
      finanzenApi.kontenplanListe(benutzerId, { status, kontoart: art, q: suche, nur_mit_hinweisen: nurHinweise ? "true" : "" }),
      finanzenApi.kontenplanZusammenfassung(benutzerId),
    ]).then(([k, z]) => { setKonten(k); setZus(z); }).catch(e => {
      if (/Berechtigung/i.test(e.message)) setKeinRecht(true); else setFehler(e.message);
      setKonten([]);
    });
  }, [benutzerId, status, art, suche, nurHinweise]);

  useEffect(() => { laden(); }, [laden]);

  const statusSetzen = async (k, neu) => {
    setFehler("");
    try { await finanzenApi.kontenplanStatus(benutzerId, k.id, neu); laden(); } catch (e) { setFehler(`Konto ${k.konto_nr}: ${e.message}`); }
  };
  const artSetzen = async (k, neu) => {
    setFehler("");
    try { await finanzenApi.kontenplanBearbeiten(benutzerId, k.id, { kontoart: neu }); laden(); } catch (e) { setFehler(`Konto ${k.konto_nr}: ${e.message}`); }
  };
  const detailOeffnen = async (k) => {
    if (offenId === k.id) { setOffenId(null); return; }
    setOffenId(k.id); setNotiz(k.notiz || "");
    try {
      const h = await finanzenApi.kontenplanHinweise(benutzerId);
      setHinweise(h.filter(x => x.konto_id === k.id));
    } catch { setHinweise([]); }
  };
  const notizSpeichern = async (k) => {
    try { await finanzenApi.kontenplanBearbeiten(benutzerId, k.id, { notiz }); laden(); } catch (e) { setFehler(e.message); }
  };
  const hinweisErledigen = async (h) => {
    try {
      await finanzenApi.kontenplanHinweisErledigt(benutzerId, h.id);
      setHinweise(hinweise.filter(x => x.id !== h.id)); laden();
    } catch (e) { setFehler(e.message); }
  };
  const exportieren = async () => {
    try {
      const blob = await finanzenApi.kontenplanExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "kontenplan.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { setFehler(e.message); }
  };

  if (keinRecht) {
    return <div style={fs.karte}><p style={{ color: THEME.colors.textMuted, margin: 0 }}>Für den Kontenplan fehlt dir das Recht „Kontenplan (SKR03)“. Das kann ein Administrator unter Einstellungen → Benutzer vergeben.</p></div>;
  }

  return (
    <div>
      <div style={fs.karte}>
        <h3 style={{ color: THEME.colors.text, margin: "0 0 6px" }}>SKR03-Kontenplan</h3>
        <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, margin: 0, maxWidth: 760, lineHeight: 1.5 }}>
          Aus den Summen- und Saldenlisten der Steuerkanzlei abgeleitet. <b>Alle Konten sind Vorschläge</b> – es wird nichts automatisch kontiert oder gebucht.
          Kontonummern mit führender Null (z.&nbsp;B. 0400) sind gegenüber der Schreibweise im PDF („400“) normalisiert; das Original bleibt sichtbar.
          Erst nach deiner Prüfung bekommt ein Konto den Status „Freigegeben“.
        </p>
        {zus && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
            <span style={{ color: THEME.colors.text }}><b>{zus.gesamt}</b> Konten</span>
            {Object.entries(zus.je_status).map(([s, n]) => <span key={s} style={{ color: STATUS_FARBE[s] }}>{STATUS_NAME[s]}: <b>{n}</b></span>)}
            <span style={{ color: zus.offene_hinweise ? THEME.colors.warning : THEME.colors.success }}>Offene Prüfhinweise: <b>{zus.offene_hinweise}</b></span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <input style={{ ...fs.input, width: 200 }} placeholder="Suche Nr. oder Name…" value={suche} onChange={e => setSuche(e.target.value)} />
        <select style={{ ...fs.input, width: "auto" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_NAME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select style={{ ...fs.input, width: "auto" }} value={art} onChange={e => setArt(e.target.value)}>
          <option value="">Alle Kontoarten</option>
          {Object.entries(KONTOART_NAME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label style={{ color: THEME.colors.textSecondary, display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={nurHinweise} onChange={e => setNurHinweise(e.target.checked)} /> nur mit offenen Hinweisen
        </label>
        <button style={{ ...fs.btnGray, width: "auto", marginLeft: "auto" }} onClick={exportieren}>⬇ Prüfliste als CSV</button>
      </div>
      {fehler && <p style={{ color: THEME.colors.danger }}>{fehler}</p>}

      <div style={fs.tabelle}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr><th style={fs.th}>Konto</th><th style={fs.th}>Bezeichnung</th><th style={fs.th}>Kontoart</th><th style={fs.th}>Status</th><th style={fs.th}>Vorkommen</th><th style={fs.th}>Hinweise</th><th style={fs.th}></th></tr>
          </thead>
          <tbody>
            {(konten || []).map(k => (
              <Fragment key={k.id}>
                <tr>
                  <td style={{ ...fs.td, fontFamily: "ui-monospace, Consolas, monospace", whiteSpace: "nowrap" }}>
                    <b>{k.konto_nr}</b>{k.konto_nr_pdf !== k.konto_nr && <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}> (PDF: {k.konto_nr_pdf})</span>}
                  </td>
                  <td style={fs.td}>{k.bezeichnung}</td>
                  <td style={fs.td}>
                    <select style={{ ...fs.input, width: "auto", padding: "4px 8px", borderColor: k.kontoart_sicher ? THEME.colors.border : THEME.colors.warning }}
                      value={k.kontoart} disabled={k.freigabe_status === "freigegeben"} onChange={e => artSetzen(k, e.target.value)}>
                      {Object.entries(KONTOART_NAME).map(([a, n]) => <option key={a} value={a}>{n}</option>)}
                    </select>
                    {!k.kontoart_sicher && <span title="Kontoart unsicher - bitte prüfen" style={{ marginLeft: 6 }}>⚠</span>}
                  </td>
                  <td style={{ ...fs.td, color: STATUS_FARBE[k.freigabe_status], fontWeight: 600 }}>{STATUS_NAME[k.freigabe_status]}</td>
                  <td style={{ ...fs.td, fontSize: THEME.fontSize.sm, color: THEME.colors.textMuted }}>{k.erstmals}–{k.zuletzt} ({k.monate_anzahl} Mon.)</td>
                  <td style={{ ...fs.td, color: k.offene_hinweise ? THEME.colors.warning : THEME.colors.textMuted }}>{k.offene_hinweise || "–"}</td>
                  <td style={{ ...fs.td, whiteSpace: "nowrap" }}>
                    <button style={{ ...fs.btnGray, width: "auto", padding: "5px 10px", marginRight: 4 }} onClick={() => detailOeffnen(k)}>Details</button>
                    {k.freigabe_status === "vorgeschlagen" && <button style={{ ...fs.btnGray, width: "auto", padding: "5px 10px", marginRight: 4 }} onClick={() => statusSetzen(k, "geprueft")}>Geprüft</button>}
                    {k.freigabe_status === "geprueft" && <button style={{ ...fs.btn, width: "auto", padding: "5px 10px", marginRight: 4 }} onClick={() => statusSetzen(k, "freigegeben")}>Freigeben</button>}
                    {k.freigabe_status !== "vorgeschlagen" && <button style={{ ...fs.btnGray, width: "auto", padding: "5px 10px" }} onClick={() => statusSetzen(k, "vorgeschlagen")}>Zurücksetzen</button>}
                  </td>
                </tr>
                {offenId === k.id && (
                  <tr>
                    <td style={fs.td} colSpan={7}>
                      {k.steuer_hinweis && <p style={{ color: THEME.colors.textSecondary, margin: "0 0 8px" }}>ℹ {k.steuer_hinweis}</p>}
                      {hinweise.length === 0 && <p style={{ color: THEME.colors.textMuted, margin: "0 0 8px" }}>Keine offenen Prüfhinweise.</p>}
                      {hinweise.map(h => (
                        <p key={h.id} style={{ color: THEME.colors.warning, margin: "0 0 6px" }}>
                          ⚠ {h.text} <button style={{ ...fs.btnGray, width: "auto", padding: "3px 8px", marginLeft: 8 }} onClick={() => hinweisErledigen(h)}>Erledigt / bestätigt</button>
                        </p>
                      ))}
                      <label style={fs.label}>Notiz</label>
                      <textarea style={{ ...fs.input, width: "100%", minHeight: 56 }} value={notiz} onChange={e => setNotiz(e.target.value)} disabled={k.freigabe_status === "freigegeben"} />
                      <button style={{ ...fs.btn, width: "auto", marginTop: 8 }} onClick={() => notizSpeichern(k)} disabled={k.freigabe_status === "freigegeben"}>Notiz speichern</button>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {konten && konten.length === 0 && !fehler && <tr><td style={fs.td} colSpan={7}>Keine Konten gefunden.</td></tr>}
            {!konten && <tr><td style={fs.td} colSpan={7}>Lädt…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
